import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { memberAPI, relationshipAPI } from '../services/api';
import { calculateDegrees } from '../utils/degreeCalculator';
import { AuthContext } from './AuthContext';

const CACHE_KEY = 'familyTreeCache';
const STALE_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds
const FOCAL_PERSON_KEY_PREFIX = 'familyTree_focalPerson_';
const LAYOUT_MODE_KEY_PREFIX = 'familyTree_layoutMode_';

const DEFAULT_DEGREE_FILTER = 4; // 1-4
const DEFAULT_LAYOUT_MODE = 'graph';  // 'graph', 'tree'


export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [members, setMembers] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focalPersonId, setFocalPersonIdState] = useState(null);
  const [degreeFilter, setDegreeFilter] = useState(DEFAULT_DEGREE_FILTER);
  const [layoutMode, setLayoutModeState] = useState(DEFAULT_LAYOUT_MODE);

  // Load cache from localStorage
  const loadCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('[DataContext] Failed to load cache:', error);
    }
    return { members: null, relationships: null };
  }, []);

  // Save cache to localStorage
  const saveCache = useCallback((key, data) => {
    try {
      const cache = loadCache();
      cache[key] = {
        data,
        fetchedAt: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('[DataContext] Failed to save cache:', error);
    }
  }, [loadCache]);

  // Check if cached data is stale
  const isStale = useCallback((cachedItem) => {
    if (!cachedItem || !cachedItem.fetchedAt) {
      return true;
    }
    return Date.now() - cachedItem.fetchedAt > STALE_TIME;
  }, []);

  // Get the current user ID for session storage key
  const getUserId = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.id;
      }
    } catch (e) {
      // Ignore parse errors
    }
    return 'anonymous';
  }, []);

  // Set focal person and persist to sessionStorage
  const setFocalPerson = useCallback((id) => {
    setFocalPersonIdState(id);
    const userId = getUserId();
    if (id !== null) {
      sessionStorage.setItem(`${FOCAL_PERSON_KEY_PREFIX}${userId}`, String(id));
    } else {
      sessionStorage.removeItem(`${FOCAL_PERSON_KEY_PREFIX}${userId}`);
    }
  }, [getUserId]);

  // Set layout mode and persist to sessionStorage
  const setLayoutMode = useCallback((mode) => {
    setLayoutModeState(mode);
    const userId = getUserId();
    sessionStorage.setItem(`${LAYOUT_MODE_KEY_PREFIX}${userId}`, mode);
  }, [getUserId]);

  // Initialize layout mode from sessionStorage
  const initializeLayoutMode = useCallback(() => {
    const userId = getUserId();
    const savedMode = sessionStorage.getItem(`${LAYOUT_MODE_KEY_PREFIX}${userId}`);
    if (savedMode && (savedMode === 'tree' || savedMode === 'graph')) {
      setLayoutModeState(savedMode);
    }
  }, [getUserId]);

  // Initialize focal person from sessionStorage or find user's member
  const initializeFocalPerson = useCallback((membersList) => {
    if (membersList.length === 0) return;

    const userId = getUserId();

    // Try to load from sessionStorage
    const savedId = sessionStorage.getItem(`${FOCAL_PERSON_KEY_PREFIX}${userId}`);
    if (savedId) {
      const savedIdNum = parseInt(savedId, 10);
      if (membersList.some(m => m.id === savedIdNum)) {
        setFocalPersonIdState(savedIdNum);
        return;
      }
    }

    // Try to find member matching current user
    // First by user_id field, then by checking created_by
    const userMember = membersList.find(m => m.user_id === parseInt(userId, 10))
      || membersList.find(m => m.created_by === parseInt(userId, 10));

    if (userMember) {
      setFocalPersonIdState(userMember.id);
      sessionStorage.setItem(`${FOCAL_PERSON_KEY_PREFIX}${userId}`, String(userMember.id));
    } else {
      // Default to first member
      setFocalPersonIdState(membersList[0].id);
    }
  }, [getUserId]);

  // Fetch members from API
  const fetchMembers = useCallback(async (silent = false) => {
    try {
      const response = await memberAPI.getAll();
      const data = response.data;
      setMembers(data);
      saveCache('members', data);
      initializeFocalPerson(data);
      return data;
    } catch (error) {
      if (!silent) {
        console.error('[DataContext] Failed to fetch members:', error);
      }
      // Silent failure - keep cached data
      return null;
    }
  }, [saveCache, initializeFocalPerson]);

  // Fetch relationships from API
  const fetchRelationships = useCallback(async (silent = false) => {
    try {
      const response = await relationshipAPI.getAll();
      const data = response.data;
      setRelationships(data);
      saveCache('relationships', data);
      return data;
    } catch (error) {
      if (!silent) {
        console.error('[DataContext] Failed to fetch relationships:', error);
      }
      // Silent failure - keep cached data
      return null;
    }
  }, [saveCache]);

  // Force refresh members
  const refreshMembers = useCallback(async () => {
    return fetchMembers(false);
  }, [fetchMembers]);

  // Force refresh relationships
  const refreshRelationships = useCallback(async () => {
    return fetchRelationships(false);
  }, [fetchRelationships]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      const cache = loadCache();

      // Initialize layout mode from sessionStorage
      initializeLayoutMode();

      // Load cached members immediately
      if (cache.members && cache.members.data) {
        setMembers(cache.members.data);
        initializeFocalPerson(cache.members.data);
      }

      // Load cached relationships immediately
      if (cache.relationships && cache.relationships.data) {
        setRelationships(cache.relationships.data);
      }

      // We have cached data, so stop loading indicator
      if (cache.members && cache.relationships) {
        setLoading(false);
      }

      // Check if members cache is stale and refresh in background
      if (isStale(cache.members)) {
        await fetchMembers(true);
      }

      // Check if relationships cache is stale and refresh in background
      if (isStale(cache.relationships)) {
        await fetchRelationships(true);
      }

      // If no cached data at all, fetch fresh
      if (!cache.members || !cache.relationships) {
        await Promise.all([
          fetchMembers(false),
          fetchRelationships(false),
        ]);
        setLoading(false);
      }
    };

    initializeData();
  }, [loadCache, isStale, fetchMembers, fetchRelationships, initializeFocalPerson, initializeLayoutMode]);

  // Handle user changes (login/logout)
  useEffect(() => {
    if (!user) {
      // User logged out - clear focal person and data
      setFocalPersonIdState(null);
      setMembers([]);
      setRelationships([]);
      setLoading(true);
    } else {
      // User logged in - re-fetch data and initialize focal person
      const reinitialize = async () => {
        setLoading(true);
        await Promise.all([
          fetchMembers(false),
          fetchRelationships(false),
        ]);
        setLoading(false);
      };
      reinitialize();
    }
  }, [user, fetchMembers, fetchRelationships]);

  // Compute degrees whenever focal person, members, or relationships change
  const memberDegrees = useMemo(() => {
    if (!focalPersonId || members.length === 0) {
      return new Map();
    }
    return calculateDegrees(members, relationships, focalPersonId);
  }, [focalPersonId, members, relationships]);

  return (
    <DataContext.Provider
      value={{
        members,
        relationships,
        loading,
        refreshMembers,
        refreshRelationships,
        focalPersonId,
        setFocalPerson,
        degreeFilter,
        setDegreeFilter,
        memberDegrees,
        layoutMode,
        setLayoutMode,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// Custom hook for consuming the context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
